import React, { PropsWithChildren, ReactElement, ReactNode } from 'react'

import { TTabsBarProps, TabsBar } from './TabsBar'
import { TUseTabs, TUseTabsProps, useTabs } from './useTabs'

export type TTabsProps<D extends Record<string, string>> = Pick<TTabsBarProps<D>, 'm' | 'dataQaIds'> &
  TUseTabsProps<D> & {
    children: ReactElement | ReactElement[] | ((props: TUseTabs<D>) => ReactElement | ReactElement[])
  }

/**
 * ### Tabs ###
 */
export const Tabs = <D extends Record<string, string>>({
  data,
  m,
  children,
  dataQaIds,
  ...otherProps
}: PropsWithChildren<TTabsProps<D>>): ReactElement => {
  const { currentTab, changeTab, getTabUrl } = useTabs({ data, ...otherProps })

  const numTabs = Object.keys(data).length

  let child: ReactNode

  if (typeof children === 'function') {
    child = children({ currentTab, changeTab, getTabUrl })
  } else {
    const childArray = React.Children.toArray(children)
    if (childArray.length !== numTabs)
      throw new Error(`TabLinks has ${childArray.length} children, but the data props has ${numTabs} entries`)
    child = childArray[Object.keys(data).findIndex((tabId) => tabId === currentTab)]
  }

  return (
    <>
      {typeof children !== 'function' ? (
        <TabsBar
          currentTab={currentTab}
          changeTab={changeTab}
          data={data}
          dataQaIds={dataQaIds}
          getTabUrl={getTabUrl}
          m={m}
        />
      ) : null}
      {child}
    </>
  )
}
